// Autosave עם debounce: שולח את המסמך המלא עם expectedRevision. השרת הוא מקור האמת.
// 409 = גרסה חדשה יותר בשרת — לא דורסים, מציגים התנגשות. כשל רשת = שגיאה + ניסיון חוזר.
import { useCallback, useEffect, useRef, useState } from 'react';
import { BannerDoc } from '@bb/schema';
import { api, ApiError } from '../../api';

export type SaveStatus = 'saved' | 'dirty' | 'saving' | 'error' | 'conflict' | 'invalid';
const DEBOUNCE_MS = 800;

export function draftKey(variantId: string) { return `bb-draft-${variantId}`; }
export function readDraft(variantId: string): { baseRevision: number; doc: BannerDoc; at: number } | null {
  try { const s = localStorage.getItem(draftKey(variantId)); return s ? JSON.parse(s) : null; } catch { return null; }
}
function writeDraft(variantId: string, baseRevision: number, doc: BannerDoc) {
  try { localStorage.setItem(draftKey(variantId), JSON.stringify({ baseRevision, doc, at: Date.now() })); } catch { /* אחסון חסום — לא קריטי */ }
}
export function clearDraft(variantId: string) { try { localStorage.removeItem(draftKey(variantId)); } catch { /* */ } }

export function useAutosave(variantId: string, doc: BannerDoc, initialRevision: number) {
  const [status, setStatus] = useState<SaveStatus>('saved');
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevisionState] = useState(initialRevision);
  const [conflictRevision, setConflictRevision] = useState<number | null>(null);
  const rev = useRef(initialRevision);
  const savedJson = useRef(JSON.stringify(doc));
  const docRef = useRef(doc);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inflight = useRef<Promise<boolean> | null>(null);
  const blocked = useRef(false); // אחרי התנגשות — לא שומרים עד טעינה מחדש
  docRef.current = doc;

  const setRevision = (r: number) => { rev.current = r; setRevisionState(r); };

  const doSave = useCallback(async (): Promise<boolean> => {
    if (blocked.current) return false;
    const current = docRef.current;
    const json = JSON.stringify(current);
    if (json === savedJson.current) { setStatus('saved'); return true; }
    const parsed = BannerDoc.safeParse(current);
    if (!parsed.success) {
      setStatus('invalid');
      setError(parsed.error.issues.map((i) => i.message).join('; '));
      return false;
    }
    setStatus('saving');
    try {
      const r = await api.patch<{ revision: number }>(`/api/variants/${variantId}`, { expectedRevision: rev.current, doc: current, summary: 'עריכה ידנית בעורך' });
      setRevision(r.revision);
      savedJson.current = json;
      setError(null);
      if (JSON.stringify(docRef.current) === json) { setStatus('saved'); clearDraft(variantId); }
      else { setStatus('dirty'); schedule(); }
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        blocked.current = true;
        setConflictRevision((e.details as any)?.currentRevision ?? null);
        setStatus('conflict');
        setError(e.message);
      } else {
        setStatus('error');
        setError(e instanceof Error ? e.message : String(e));
      }
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantId]);

  const run = useCallback((): Promise<boolean> => {
    if (inflight.current) return inflight.current.then(() => run());
    const p = doSave().finally(() => { inflight.current = null; });
    inflight.current = p;
    return p;
  }, [doSave]);

  function schedule() {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; void run(); }, DEBOUNCE_MS);
  }

  useEffect(() => {
    if (blocked.current) return;
    const json = JSON.stringify(doc);
    if (json === savedJson.current) { if (!inflight.current) setStatus('saved'); return; }
    setStatus('dirty');
    writeDraft(variantId, rev.current, doc);
    schedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, variantId]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // אזהרה לפני יציאה עם שינויים שלא נשמרו
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (status === 'dirty' || status === 'saving' || status === 'error') { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [status]);

  /** שמירה מיידית (לפני AI, תצוגה מקדימה, חזרה לגרסה). מחזיר את מספר הגרסה השמורה, או null אם נכשל */
  const flush = useCallback(async (): Promise<number | null> => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    return (await run()) ? rev.current : null;
  }, [run]);

  /** אימוץ גרסה שנוצרה בשרת (AI, חזרה, התאמה) — המסמך הזה נחשב שמור */
  const adopt = useCallback((r: number, d: BannerDoc) => {
    setRevision(r);
    savedJson.current = JSON.stringify(d);
    blocked.current = false;
    setConflictRevision(null);
    setError(null);
    setStatus('saved');
    clearDraft(variantId);
  }, [variantId]);

  return { status, error, revision, conflictRevision, flush, retry: flush, adopt };
}
